import { File, Folder } from "lucide-react";
import { useEffect, useState } from "react";
import { useFiles } from "@/api/hooks";

interface FileMentionProps {
  deskId: string;
  inputValue: string;
  cursorPosition: number;
  onSelect: (replacement: string, from: number, to: number) => void;
  onClose: () => void;
}

/**
 * Inline file-mention dropdown for the composer. Anchored to the composer
 * (positioned absolutely by the parent via `relative`). Listens to
 * ArrowUp/Down/Enter/Tab/Escape globally while open; mouse selection via
 * onMouseDown (preventDefault so the input doesn't lose focus).
 *
 * Trigger convention: the parent opens this when the input has an `@` or
 * `/` at the cursor, preceded by whitespace or start-of-string. The
 * characters between the trigger and the cursor form the filter; a `/`
 * inside the filter navigates into that subdirectory.
 */
export function FileMention({
  deskId,
  inputValue,
  cursorPosition,
  onSelect,
  onClose,
}: FileMentionProps) {
  const [selectedIdx, setSelectedIdx] = useState(0);

  const before = inputValue.slice(0, cursorPosition);
  const atIdx = before.lastIndexOf("@");
  const slashIdx = before.lastIndexOf("/");
  const atValid = atIdx !== -1 && (atIdx === 0 || before[atIdx - 1] === " ");
  const slashValid =
    slashIdx !== -1 && (slashIdx === 0 || before[slashIdx - 1] === " ");

  const triggerIdx =
    atValid && slashValid
      ? Math.max(atIdx, slashIdx)
      : atValid
        ? atIdx
        : slashValid
          ? slashIdx
          : -1;

  const rawQuery = triggerIdx >= 0 ? before.slice(triggerIdx + 1) : "";
  const lastSlash = rawQuery.lastIndexOf("/");
  const browseDir = lastSlash >= 0 ? `/${rawQuery.slice(0, lastSlash)}` : "/";
  const filterQuery = (
    lastSlash >= 0 ? rawQuery.slice(lastSlash + 1) : rawQuery
  ).toLowerCase();

  const { data } = useFiles(deskId, browseDir);

  const entries = (data?.entries ?? []).filter(
    (e) => !e.name.startsWith(".") && e.name !== "node_modules",
  );
  const filtered = filterQuery
    ? entries
        .filter((e) => e.name.toLowerCase().includes(filterQuery))
        .slice(0, 8)
    : entries.slice(0, 8);

  // Only reset selection when the filter actually changes the result count.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional shallow dep
  useEffect(() => setSelectedIdx(0), [filtered.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIdx((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIdx((i) => Math.max(i - 1, 0));
      } else if (
        (e.key === "Enter" || e.key === "Tab") &&
        filtered[selectedIdx]
      ) {
        e.preventDefault();
        const entry = filtered[selectedIdx];
        const ref = entry.path.startsWith("/") ? entry.path : `/${entry.path}`;
        // Directories append "/" so the user can keep typing to descend
        onSelect(
          entry.isDir ? `${ref}/` : `${ref} `,
          triggerIdx,
          cursorPosition,
        );
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [filtered, selectedIdx, triggerIdx, cursorPosition, onSelect, onClose]);

  if (filtered.length === 0) return null;

  return (
    <div
      className="absolute bottom-full left-0 right-0 mb-1 rounded-lg max-h-52 overflow-y-auto z-50 animate-fade-in"
      style={{
        background: "var(--white)",
        border: "1px solid var(--border)",
        boxShadow: "0 8px 24px rgba(26, 18, 16, 0.08)",
      }}
    >
      <div className="p-1">
        {filtered.map((entry, i) => (
          <button
            key={entry.path}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              const ref = entry.path.startsWith("/")
                ? entry.path
                : `/${entry.path}`;
              onSelect(
                entry.isDir ? `${ref}/` : `${ref} `,
                triggerIdx,
                cursorPosition,
              );
            }}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs text-left"
            style={{
              background:
                i === selectedIdx
                  ? "color-mix(in srgb, var(--accent) 10%, transparent)"
                  : "transparent",
              color: i === selectedIdx ? "var(--accent)" : "var(--t2)",
            }}
            onMouseEnter={(e) => {
              if (i !== selectedIdx) {
                (e.currentTarget as HTMLElement).style.background =
                  "var(--hover)";
              }
            }}
            onMouseLeave={(e) => {
              if (i !== selectedIdx) {
                (e.currentTarget as HTMLElement).style.background =
                  "transparent";
              }
            }}
          >
            {entry.isDir ? (
              <Folder
                size={12}
                className="shrink-0"
                style={{ color: "var(--accent)" }}
              />
            ) : (
              <File
                size={12}
                className="shrink-0"
                style={{ color: "var(--t3)" }}
              />
            )}
            <span
              className="truncate font-mono"
              style={{ fontFamily: "var(--font-mono)" }}
            >
              {entry.name}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
